import {hasValue} from '@famly/stat_ts-utils_has-value';
import {getFromLocalStorage} from '@famly/stat_ts-utils_local-storage';

export type Crew = 'enr' | 'stat' | 'fin' | 'inv' | 'pay' | 'gro' | 'aim' | 'tail' | 'go' | 'nux' | 'ai';

export type FeatureFlag = {
    flag: string;
    title: string;
    description?: string;
    crew: Crew;
};

export type ToolingFlag = {
    flag: string;
    title: string;
    description?: string;
};

export type DebuggerFlags = {
    [flagId: string]: FeatureFlag | ToolingFlag;
};

export const DebugFeatureLocalStorageFlags = {
    AREA_MANAGEMENT: {
        flag: 'AREA_MANAGEMENT',
        title: 'Area management',
        description: 'Toggle this to show the area management page in the organization settings.',
        crew: 'go',
    },
    SHOW_DEFAULT_FTE: {
        flag: 'SHOW_DEFAULT_FTE',
        title: 'Show default FTE',
        description: 'Toggle this to show default fte.',
        crew: 'stat',
    },
    UNIQUE_PARENT_EXPERIENCE: {
        flag: 'UNIQUE_PARENT_EXPERIENCE',
        title: 'Unique parent experience home and explore',
        description: 'Toggle this to show unique parent experience home and explore.',
        crew: 'enr',
    },
    SHOW_FINANCE_DEBUGGING_BUTTON: {
        flag: 'SHOW_FINANCE_DEBUGGING_BUTTON',
        title: 'Show finance debugging button',
        description: 'Show finance debugging button to access Finance debugging page.',
        crew: 'fin',
    },
    SHOW_BULK_PLAN_DELETE: {
        flag: 'SHOW_BULK_PLAN_DELETE',
        title: 'Show Bulk Plan delete option',
        description: 'Toggle this to show the bulk delete latest plan in plans page',
        crew: 'inv',
    },
    SHOW_BULK_BILLING_PROFILE_UPDATE: {
        flag: 'SHOW_BULK_BILLING_PROFILE_UPDATE',
        title: 'Show Bulk Billing Profile Update in the Plans page',
        description: 'Used to handle bulk billing profile update in the Plans page',
        crew: 'inv',
    },
    ENABLE_MIGRATE_V1_TO_V2: {
        flag: 'ENABLE_MIGRATE_V1_TO_V2',
        title: 'Enable V1 to V2 billing profile migration',
        description:
            'Toggle this on to enable the bulk migration flow from V1 billing profiles (ACTUAL_SESSIONS, ANNUALIZED, ANNUALIZED_MID_MONTH) to their V2 equivalents (ACTUALS_V2, ANNUALIZED_V2).',
        crew: 'inv',
    },
    FUNDING_CONTACT_PREFILL: {
        flag: 'FUNDING_CONTACT_PREFILL',
        title: 'Funding contact prefill',
        description:
            'Toggle this on to let the Automatic funding tab of the Funding report give a Funding contact to every child at the site that has none.',
        crew: 'inv',
    },
    NEW_FORM_SUBMIT_PAGE_LAYOUT: {
        flag: 'NEW_FORM_SUBMIT_PAGE_LAYOUT',
        title: 'New form submit page layout',
        description: 'Turn this on to enable the new form submit page layout.',
        crew: 'aim',
    },
    FOLLOWUP_FORM_FEES: {
        flag: 'FOLLOWUP_FORM_FEES',
        title: 'Follow-up form fees',
        description: 'Turn this on to enable payments in follow-up forms',
        crew: 'gro',
    },
    MOBILE_APP_PACKAGE_PURCHASE_MAGIC_LINK_REDIRECT: {
        flag: 'MOBILE_APP_PACKAGE_PURCHASE_MAGIC_LINK_REDIRECT',
        title: 'Mobile app package purchase magic link redirect',
        crew: 'gro',
    },
    MEAL_PLANS_DND: {
        flag: 'MEAL_PLANS_DND',
        title: 'Enable dnd of food items in meal plans',
        crew: 'aim',
    },
    BULK_ADD_ATTENDANCE: {
        flag: 'BULK_ADD_ATTENDANCE',
        title: 'Show the bulk add attendance modal',
        crew: 'stat',
    },
    SHIFT_CONTRACT_INFORMED_ABSENCE: {
        flag: 'SHIFT_CONTRACT_INFORMED_ABSENCE',
        title: 'Show the new Shift & Contract Informed Absence',
        crew: 'stat',
    },
    SMS_NOTIFICATIONS: {
        flag: 'SMS_NOTIFICATIONS',
        title: 'Enable SMS notifications in Newsfeed and Messages',
        crew: 'aim',
    },
    SHOW_PURCHASES_NEW_DESIGN_ON_MOBILE: {
        flag: 'SHOW_PURCHASES_NEW_DESIGN_ON_MOBILE',
        title: 'Show new design for purchases on mobile and automatic pricing',
        crew: 'gro',
    },
    MF_LISTS_MANAGE_CHILDREN: {
        flag: 'MF_LISTS_MANAGE_CHILDREN',
        title: 'Enable manage children MF List implementation',
        crew: 'aim',
    },
    GO_CHILD_OVERVIEW: {
        flag: 'GO_CHILD_OVERVIEW',
        title: 'Enable the new Children Overview route',
        description:
            'Serves the GO rework of "Manage children" on /account/institution/:institutionId/children-overview.',
        crew: 'go',
    },
    GO_KIVAN_IMPORT_V2: {
        flag: 'GO_KIVAN_IMPORT_V2',
        title: 'Enable Kivan import v2 (source chooser + Kivan API import)',
        description:
            'Shows the Kivan import v2 button and its source-chooser modal on the children overview, gating the dual-source (Excel + Kivan API) import flow.',
        crew: 'go',
    },
    GO_INQUIRY_IMPORT: {
        flag: 'GO_INQUIRY_IMPORT',
        title: 'Enable inquiry (enquiry) spreadsheet import',
        description:
            'Shows the inquiry import button on the inquiries page, gating the spreadsheet-based inquiry import wizard.',
        crew: 'go',
    },
    GO_STAFF_DOCUMENT_ACKNOWLEDGEMENT: {
        flag: 'GO_STAFF_DOCUMENT_ACKNOWLEDGEMENT',
        title: 'Enable staff document acknowledgement',
        description:
            'Adds an acknowledgement status column, an acknowledgement drawer and a "Require acknowledgement" action to Staff documents. Also needs the STAFF_DOCUMENT_ACKNOWLEDGEMENT site feature.',
        crew: 'go',
    },
    NEW_REPORT_CREATE_FLOW: {
        flag: 'NEW_REPORT_CREATE_FLOW',
        title: 'Enable new "Create report" flow',
        description: 'Enables the new "Create report" flow on reports pages.',
        crew: 'aim',
    },
    LIMITED_CONTACT_ROLE: {
        flag: 'LIMITED_CONTACT_ROLE',
        title: 'Enable Limited Contact Role toggle',
        description: 'Shows the Limited Contact Role toggle in the child profile contacts form.',
        crew: 'aim',
    },
    SLEEP_MODAL_NOTES: {
        flag: 'SLEEP_MODAL_NOTES',
        title: 'Enable notes field in sleep modal',
        description: 'Shows the optional notes text field in the sleep registration modal.',
        crew: 'aim',
    },
    BILL_PAYER_ENABLE_LEGACY_CSV_REPORT: {
        flag: 'BILL_PAYER_ENABLE_LEGACY_CSV_REPORT',
        title: 'Enables the legacy PHP-based CSV report for bill payers',
        description:
            'While testing the new BillPayer report, it might be useful to have the old one available as well for testing. This enables the legacy report.',
        crew: 'pay',
    },
    FAMLY_HOME_NEW_LAYOUT: {
        flag: 'FAMLY_HOME_NEW_LAYOUT',
        title: 'Enable new Famly Home layout (prototype)',
        description: 'The new layout will remove the sidebar and move all widgets into the primary widget area',
        crew: 'aim',
    },
    NEW_PROFILE_MENU_LOCATION: {
        flag: 'NEW_PROFILE_MENU_LOCATION',
        title: 'Enable new profile menu location',
        description:
            'Toggle this on to use the new location in the sidebar for the profile menu. Also hides the profile menu in the topbar.',
        crew: 'aim',
    },
    INTENDED_AUDIENCE_FOR_ACTIVITIES_AND_RESOURCES: {
        flag: 'INTENDED_AUDIENCE_FOR_ACTIVITIES_AND_RESOURCES',
        title: 'Enable intended audience for activities and resources',
        description: 'Toggle this on to enable intended audience for activities and resources.',
        crew: 'aim',
    },
    DISABLE_CHILD_PROFILE_INSERT_PLAN_DIVIDERS: {
        flag: 'DISABLE_CHILD_PROFILE_INSERT_PLAN_DIVIDERS',
        title: 'Disable insert plan dividers in Child Profile',
        description:
            'Disables the dividers in the plan overview in the Child Profile for inserting plans between existing plans.',
        crew: 'aim',
    },
    NEW_FUNDING_LOOP: {
        flag: 'NEW_FUNDING_LOOP',
        title: 'Enable new funding loop tabs',
        description: 'Toggle this on to enable the new funding loop tabs in the public funding report.',
        crew: 'inv',
    },
    REDESIGN_CHART_OF_ACCOUNTS: {
        flag: 'REDESIGN_CHART_OF_ACCOUNTS',
        title: 'Enable redesign of chart of accounts',
        description: 'Toggle this on to enable the redesign of chart of accounts.',
        crew: 'inv',
    },
    PRICING_STRUCTURE: {
        flag: 'PRICING_STRUCTURE',
        title: 'Enable modern pricing structure page',
        description:
            'Toggle this on to render the modern non-DACH pricing structure settings page instead of the legacy "Items & Pricing" page.',
        crew: 'inv',
    },
    MANAGE_EXTERNAL_IDS: {
        flag: 'MANAGE_EXTERNAL_IDS',
        title: 'Show Manage External IDs component',
        description: 'Toggle this on to show the Manage External IDs component in integration settings.',
        crew: 'aim',
    },
    DEVICE_ENROLLMENT: {
        flag: 'DEVICE_ENROLLMENT',
        title: 'Device Enrollment',
        description: 'Toggle this on to enable device enrollment features.',
        crew: 'aim',
    },
    NAVIGATION_DESIGN_V3: {
        flag: 'NAVIGATION_DESIGN_V3',
        title: 'Use new V3 navigation design',
        description: 'Use new light V3 navigation design.',
        crew: 'gro',
    },
    ASSET_UPLOAD_PLAYGROUND: {
        flag: 'ASSET_UPLOAD_PLAYGROUND',
        title: 'Show Asset Upload Playground',
        description: 'Toggle this on to show the asset upload playground in Settings.',
        crew: 'go',
    },
    SIDEKICK_ASSISTANT_SITE_SETTINGS: {
        flag: 'SIDEKICK_ASSISTANT_SITE_SETTINGS',
        title: 'Show Sidekick Assistant site settings page',
        description:
            'Toggle this on to show the Sidekick Assistant site settings page (Knowledge) under Feature Settings, and the Site settings entry in the Sidekick drawer.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_WHILE_IMPERSONATING: {
        flag: 'SIDEKICK_ASSISTANT_WHILE_IMPERSONATING',
        title: 'Show Sidekick Assistant while impersonating',
        description:
            'Toggle this on to show the Sidekick Assistant while impersonating a customer. Sidekick is hidden during impersonation by default so it never surfaces to customers; internal staff dogfooding Sidekick in a customer context must opt in with this flag.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_AGENT_SWITCHER: {
        flag: 'SIDEKICK_ASSISTANT_AGENT_SWITCHER',
        title: 'Show Sidekick agent switcher',
        description:
            'Toggle this on to show a dev-only dropdown in the Sidekick drawer for switching between the daycare (default) and finance agents.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_WORKFLOWS: {
        flag: 'SIDEKICK_ASSISTANT_WORKFLOWS',
        title: 'Show Sidekick workflow picker',
        description:
            'Toggle this on to show a dev-only dropdown in the Sidekick composer for starting a workflow. Picking one switches to the agent that runs it, starts a fresh chat, and pre-fills the composer with the workflow prompt.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_AUTOMATIONS: {
        flag: 'SIDEKICK_ASSISTANT_AUTOMATIONS',
        title: 'Show Sidekick automation run log',
        description:
            'Toggle this on to show the Automations card in personal Sidekick settings, listing recent background runs with their status and a re-run action.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_AUTOMATION_RUNS: {
        flag: 'SIDEKICK_ASSISTANT_AUTOMATION_RUNS',
        title: 'Show Sidekick automation-run presentation',
        description:
            'Toggle this on to switch the Sidekick drawer to the automation-run presentation: a run card replacing the machine-generated trigger prompt, an unread-conversation banner at the top of the chat, and an unread badge derived from the conversation list that ignores still-running automation runs.',
        crew: 'ai',
    },
    SIDEKICK_AGENT_CATALOG: {
        flag: 'SIDEKICK_AGENT_CATALOG',
        title: 'Show Sidekick agent catalog page',
        description:
            'Toggle this on to show the "Meet your agents" catalog page and its entry points. Requires Sidekick access on the site.',
        crew: 'ai',
    },
    FORCE_NOTIFICATION_POLLING: {
        flag: 'FORCE_NOTIFICATION_POLLING',
        title: 'Force notification polling in e2e',
        description: 'Toggle this on to force notification count polling even in e2e mode.',
        crew: 'aim',
    },
    SHOW_FUNDING_IN_CHILD_PROFILE_WITH_DRC: {
        flag: 'SHOW_FUNDING_IN_CHILD_PROFILE_WITH_DRC',
        title: 'Show Funding section in child profile when DRC is enabled',
        description:
            'Shows the Funding section in the child profile even when DAILY_RATE_WITH_COPAY_FUNDING (DRC) is enabled on the site. By default the section is hidden on DRC sites because the DRC flow replaces it; this flag is for testers who need to see the old Funding section while DRC stays on.',
        crew: 'gro',
    },
    SUBSIDY_RECONCILIATION_REPORTS: {
        flag: 'SUBSIDY_RECONCILIATION_REPORTS',
        title: 'Show subsidy reconciliation reports',
        description:
            'Shows the new sub-tabs on the US Subsidies page (Subsidy payments, Differences, Expected subsidies) while they run on mock data. Without the flag the page shows only the live Expected subsidies report.',
        crew: 'gro',
    },
    USE_NEW_BILL_PAYER_FORMS: {
        flag: 'USE_NEW_BILL_PAYER_FORMS',
        title: 'Use the new bill payer forms',
        description:
            'Routes bill-payer entry points to the new react-hook-form modals/forms (PAY bill-payer-forms revamp) instead of the legacy redux-form ones. Has no effect until the new forms are wired up to entry points.',
        crew: 'pay',
    },
    SHOW_UK_ENTITLEMENTS_OPT_IN: {
        flag: 'SHOW_UK_ENTITLEMENTS_OPT_IN',
        title: 'Show entitlement-based funding opt-in',
        description:
            'Shows the "Entitlement-based funding" opt-in box in the Playground while the feature is in testing. Remove this flag once the feature ships.',
        crew: 'inv',
    },
} as const satisfies Record<string, FeatureFlag>;

export const ToolingLocalStorageFlags = {
    STICKY_DEBUGGING: {
        flag: 'STICKY_DEBUGGING',
        title: 'Always show the "Debugging" menu',
        description: 'Toggle this on to always show the debugging menu.',
    },
    ENABLE_INTERCOM_TEST_ENVIRONMENT: {
        flag: 'ENABLE_INTERCOM_TEST_ENVIRONMENT',
        title: 'Connect to Intercom test environment',
        description: 'Connect to Intercom test environment for debugging/dev purposes.',
    },
    SHOW_AUTO_LOGOUT_STATS: {
        flag: 'SHOW_AUTO_LOGOUT_STATS',
        title: 'Auto logout V2 status',
        description:
            'Toggle this to get an overlay that shows all details for auto logout. Auto logout feature and V2 needs to be enabled.',
    },
    TRACKING_VISUALIZATION: {
        flag: 'TRACKING_VISUALIZATION',
        title: 'Enable tracking visualisation of Intercom and Mixpanel events',
        description:
            'Enables the unified tracking visualisation tool for both Mixpanel and Intercom events. You can see tracking events for elements, control which events are shown/sent, and view a searchable live feed of events. Make sure the feature "ENABLE_ANALYTICS" is enabled as well.',
    },
    FORM_BUILDER_DEBUG_INFO: {
        flag: 'FORM_BUILDER_DEBUG_INFO',
        title: 'Adds debug info to the form builder state (for e2e)',
        description: 'Toggle this to add debug info to the form builder state (for e2e)',
    },
    TRACE_ALL_TRANSACTIONS: {
        flag: 'TRACE_ALL_TRANSACTIONS',
        title: '[Sentry] Trace all transactions',
        description: 'Turn this on to have all transactions traced in Sentry.',
    },
    SENTRY_ENABLE_REPLAY: {
        flag: 'ENABLE_REPLAY',
        title: '[Sentry] Enable Replay',
        description: 'Turn this on to enable Sentry replay.',
    },
    SENTRY_DIAGNOSTIC_MODE: {
        flag: 'SENTRY_DIAGNOSTIC_MODE',
        title: '[Sentry] Diagnostic mode',
        description:
            "Turn this on to enable Sentry diagnostic mode. This will bypass the filtering we've setup in sentry-setup.ts.",
    },
    SHOW_STAT_FORM_DEBUG: {
        flag: 'SHOW_STAT_FORM_DEBUG',
        title: 'Enable stat form debug information',
    },
    ENABLE_FORM_BUILDER_DEBUG: {
        flag: 'ENABLE_FORM_BUILDER_DEBUG',
        title: 'Enable form builder debug info',
        description: 'Toggle this on to access the form builder debug info',
    },
    ENABLE_IMAGICK_IMAGE_PROCESSING: {
        flag: 'ENABLE_IMAGICK_IMAGE_PROCESSING',
        title: 'Enable Imagick image processing',
        description: 'Toggle this on to use Imagick for image processing instead of GD (in core).',
    },
    APOLLO_DEV_ERROR_MESSAGES: {
        flag: 'APOLLO_DEV_ERROR_MESSAGES',
        title: '[Apollo] Load dev error messages',
        description:
            'Toggle this on to load the verbose Apollo Client dev error messages outside of e2e (helpful when debugging minified Apollo errors locally or in production).',
    },
    FORM_FILL_DEBUGGING: {
        flag: 'FORM_FILL_DEBUGGING',
        title: 'Form fill button',
        description:
            'Shows a floating "Fill form" button that fills all visible, empty form fields with dummy data. Useful for quickly getting past mandatory fields when testing.',
    },
} as const satisfies Record<string, ToolingFlag>;

export const DebugLocalStorageFlags = {
    ...DebugFeatureLocalStorageFlags,
    ...ToolingLocalStorageFlags,
};

/**
 * I grew tired of having to import DebugLocalStorageFlags to check if a flag is enabled
 * so I added an overload that allows you to simply look up by the union of keys in DebugLocalStorageFlags
 */
type CheckIfFlagIsToggled = {
    (flag: string): boolean;
    (flag: keyof typeof DebugLocalStorageFlags): boolean;
};
export const checkIfFlagIsToggled: CheckIfFlagIsToggled = flag => {
    const lookup = DebugLocalStorageFlags[flag as keyof typeof DebugLocalStorageFlags];
    if (hasValue(lookup)) {
        return Boolean(getFromLocalStorage(lookup.flag));
    }
    return Boolean(getFromLocalStorage(flag));
};
